import React, { useCallback, useMemo, useState } from 'react';

import type { UUID } from '@aline/common';
import {
  defaultWorkflowFileSort,
  getEditorWorkflowRoute,
  getIsWorkflowPhased,
  Select,
} from '@aline/common';
import { link } from '@aline/routes';
import { CircleCheck, Clock, ListTodo, Signature } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineTooltip, ConfirmationDialog } from 'src/components';
import { SanityCheckModal } from 'src/components/send-for-signature/components/sanity-check-modal';
import { getAreAllTasksDone } from 'src/components/workflow/board/workflow-card/task-progress';
import { WorkflowPhasesCompleteCard } from 'src/components/workflow/workflow-detail/phases/WorkflowPhasesCompleteCard';
import { Apollo } from 'src/gql/generated';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { useCurrentUserQuery } from 'src/gql/hooks/user';
import { useSendForSignatureWithChecks } from 'src/hooks/workflow/send-for-signature';
import {
  useGetWorkflowFiles,
  useRevertWorkflowToDraft,
  useStartWorkflowWork,
} from 'src/hooks/workflow/workflow';
import { getIsSigningPhaseType } from 'src/workflow/helpers';
import { makeStyles } from 'tss-react/mui';

import type { WorkflowBannerSeverity } from './WorkflowBannerCard';
import { WorkflowBannerAction, WorkflowBannerCard } from './WorkflowBannerCard';
import { useWorkflowDetailLinks } from './WorkflowDetailContext';

interface BannerAction {
  label: string;
  onClick: () => void;
  dataCy?: string;
  disabledReason?: string;
  loading?: boolean;
  tooltip?: string;
}

const useBannerStyles = makeStyles()(() => ({
  bannerInset: {
    boxSizing: 'border-box',
    width: '100%',
  },
}));

interface BannerShellProps {
  heading: string;
  icon: React.ComponentType<{ size?: number }>;
  severity: WorkflowBannerSeverity;
  subtitle: string;
  action?: React.ReactNode;
  dataCy?: string;
}

function BannerShell(props: BannerShellProps) {
  const { classes } = useBannerStyles();
  return (
    <div className={classes.bannerInset}>
      <WorkflowBannerCard {...props} />
    </div>
  );
}

function AlertActions({
  primary,
  secondary,
}: {
  primary?: BannerAction;
  secondary?: BannerAction;
}) {
  if (!primary && !secondary) return undefined;
  return (
    <>
      {secondary && (
        <WorkflowBannerAction secondary dataCy={secondary.dataCy} onClick={secondary.onClick}>
          {secondary.label}
        </WorkflowBannerAction>
      )}
      {primary && (
        <AlineTooltip arrow title={primary.tooltip ?? primary.disabledReason ?? ''}>
          <span>
            <WorkflowBannerAction
              dataCy={primary.dataCy}
              disabled={Boolean(primary.disabledReason)}
              loading={primary.loading}
              onClick={primary.onClick}
            >
              {primary.label}
            </WorkflowBannerAction>
          </span>
        </AlineTooltip>
      )}
    </>
  );
}

function tasksSubtitle(count: number): string {
  return count === 1 ? '1 task needs your attention.' : `${count} tasks need your attention.`;
}

interface NegotiatingBannerProps {
  phases: ApolloTypes.PhaseSimpleFragment[];
  workflowId: UUID;
}

function NegotiatingBanner({ phases, workflowId }: NegotiatingBannerProps) {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUserQuery();
  const myPendingTasks = useMemo(() => {
    const activePhase = phases.find(phase => phase.startedAt != null && !phase.completedAt);
    if (activePhase?.__typename !== 'NonSigningPhase') return [];

    return activePhase.tasks
      .filter(
        t =>
          t.assignments.some(
            ({ permission }) =>
              permission.account?.__typename === 'User' && permission.account.id === currentUser?.id
          ) && !t.completedAt
      )
      .map(task => ({ phase: activePhase, task }));
  }, [currentUser?.id, phases]);

  const handleGoToTask = useCallback(async () => {
    const first = myPendingTasks.at(0);
    if (!first) return;

    const { task } = first;
    navigate(link.WorkflowDetailTabTask({ taskId: task.id, workflowId }));
  }, [myPendingTasks, navigate, workflowId]);

  if (!myPendingTasks.length) return null;

  return (
    <BannerShell
      action={
        <AlertActions
          primary={{
            label: 'Get Started',
            onClick: handleGoToTask,
          }}
        />
      }
      dataCy={Select.workflow.detail.negotiatingBanner}
      heading="You have tasks to complete"
      icon={ListTodo}
      severity="info"
      subtitle={tasksSubtitle(myPendingTasks.length)}
    />
  );
}

interface ReadyForSignatureBannerProps {
  workflowId: UUID;
}

function ReadyForSignatureBanner({ workflowId }: ReadyForSignatureBannerProps) {
  const navigate = useNavigate();
  const { workflowFiles } = useGetWorkflowFiles(workflowId);
  const { isSubmitting, openDialog, sendForSignatureWithChecks, setOpenDialog } =
    useSendForSignatureWithChecks({
      showNotReadyReasons: true,
      workflowId,
    });

  return (
    <>
      <BannerShell
        action={
          <AlertActions
            primary={{
              dataCy: Select.workflow.detail.toolbar.sendForSignatureButton,
              label: 'Send for Signature' as const,
              loading: isSubmitting,
              onClick: sendForSignatureWithChecks,
            }}
          />
        }
        dataCy={Select.workflow.detail.readyForSignatureBanner}
        heading="Ready for signature"
        icon={Signature}
        severity="info"
        subtitle="All tasks are complete. Send this workflow for signature."
      />
      <SanityCheckModal
        openDialogTuple={[openDialog, setOpenDialog]}
        workflowId={workflowId}
        onClickFixMissingActions={() => {
          const firstFile = workflowFiles.at(0);
          if (firstFile) navigate(getEditorWorkflowRoute(firstFile.document.id, workflowId));
        }}
        onClose={() => setOpenDialog(null)}
      />
    </>
  );
}

interface SigningBannerProps {
  phases: ApolloTypes.PhaseSimpleFragment[];
  workflowId: UUID;
}

const ACTIVE_SIGNER_STATUSES = new Set<string>([
  Apollo.WorkflowRecipientStatus.Sent,
  Apollo.WorkflowRecipientStatus.Delivered,
]);

function SigningBanner({ phases, workflowId }: SigningBannerProps) {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUserQuery();
  const { workflowFiles } = useGetWorkflowFiles(workflowId);
  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const { revertToDraft } = useRevertWorkflowToDraft();
  const isCurrentUserActiveSigner = useMemo(() => {
    return phases
      .flatMap(p => {
        if (p.__typename !== 'SigningPhase') return [];
        return p.steps.flatMap(s => s.actions);
      })
      .some(a => ACTIVE_SIGNER_STATUSES.has(a.status) && a.assignee?.id === currentUser?.id);
  }, [currentUser?.id, phases]);

  function handleSign() {
    const firstFile = [...workflowFiles].sort(defaultWorkflowFileSort).at(0);
    if (firstFile)
      navigate(
        getEditorWorkflowRoute(firstFile.document.id, workflowId, {
          walkThroughParams: { autoStartWalkthrough: true },
        })
      );
  }

  async function handleRevert() {
    await revertToDraft({ variables: { workflowId } });
    setRevertConfirmOpen(false);
  }

  const revertAction = {
    dataCy: Select.workflow.detail.toolbar.revertToDraftButton,
    label: 'Revert to Draft' as const,
    onClick: () => setRevertConfirmOpen(true),
  };

  return (
    <>
      <BannerShell
        action={
          isCurrentUserActiveSigner ? (
            <AlertActions
              primary={{ label: 'Sign', onClick: handleSign }}
              secondary={revertAction}
            />
          ) : (
            <AlertActions primary={revertAction} />
          )
        }
        dataCy={Select.workflow.detail.signingBanner}
        heading={isCurrentUserActiveSigner ? 'Your signature is needed' : 'Signing in progress'}
        icon={isCurrentUserActiveSigner ? Signature : Clock}
        severity="info"
        subtitle={
          isCurrentUserActiveSigner
            ? 'This workflow is waiting for your signature.'
            : 'This workflow has been sent for signature.'
        }
      />
      <ConfirmationDialog
        message="Are you sure you want to revert the workflow back to drafting? All documents and the workflow will be retained, but any signing progress will be cleared. This action cannot be undone."
        open={revertConfirmOpen}
        primaryText="Revert"
        title="Revert to drafting?"
        onClickPrimary={() => void handleRevert()}
        onClose={() => setRevertConfirmOpen(false)}
      />
    </>
  );
}

type WorkflowStatusBannerWorkflow = Pick<
  Apollo.Workflow,
  '__typename' | 'id' | 'completedAt' | 'createdAt' | 'startedAt' | 'state' | 'variant'
>;

interface WorkflowStatusBannerProps {
  phases: ApolloTypes.PhaseSimpleFragment[];
  workflow: WorkflowStatusBannerWorkflow;
}

interface StartWorkBannerProps {
  workflowId: UUID;
}

function StartWorkBanner({ workflowId }: StartWorkBannerProps) {
  const { loading, startWorkflowWork } = useStartWorkflowWork(workflowId);

  return (
    <BannerShell
      action={
        <AlertActions
          primary={{
            label: 'Start',
            loading,
            onClick: () => startWorkflowWork(),
          }}
        />
      }
      dataCy="workflow-start-work-banner"
      heading="Ready to begin"
      icon={ListTodo}
      severity="info"
      subtitle={`This workflow is unstarted. Click "Start" to begin the first phase.`}
    />
  );
}

export const WorkflowStatusBanner = React.memo(function WorkflowStatusBanner({
  phases,
  workflow,
}: WorkflowStatusBannerProps) {
  const isTemplateWorkflow = workflow.variant === Apollo.WorkflowVariant.Template;

  if (isTemplateWorkflow) return null;
  const isPhased = getIsWorkflowPhased(phases, getIsSigningPhaseType);
  // A phaseless workflow's task list already shows when everything is done.
  if (workflow.state === Apollo.WorkflowState.Complete && !isPhased) return null;
  if (workflow.state === Apollo.WorkflowState.Complete)
    return (
      <WorkflowStatusBannerInset>
        <WorkflowPhasesCompleteCard workflow={workflow} />
      </WorkflowStatusBannerInset>
    );

  // A phaseless workflow's tasks start on their own; starting one then offers to start the workflow.
  if (!workflow.startedAt && !isPhased) return null;
  if (!workflow.startedAt)
    return (
      <WorkflowStatusBannerInset>
        <StartWorkBanner workflowId={workflow.id} />
      </WorkflowStatusBannerInset>
    );

  if (getIsSinglePhaseAllDone(phases))
    return (
      <WorkflowStatusBannerInset>
        <AllTasksDoneBanner workflowId={workflow.id} />
      </WorkflowStatusBannerInset>
    );

  return (
    <RequiredActionsBannerOrFallback
      phases={phases}
      workflowId={workflow.id}
      workflowState={workflow.state}
    />
  );
});

/**
 * With one phase there is no next phase to hand off to, so finishing the last
 * task leaves the card where it is. The owner moves it to Done from here.
 */
function getIsSinglePhaseAllDone(phases: ApolloTypes.PhaseSimpleFragment[]): boolean {
  const [onlyPhase, ...rest] = phases;
  if (rest.length || onlyPhase?.__typename !== 'NonSigningPhase') return false;
  return getAreAllTasksDone(onlyPhase.tasks);
}

function AllTasksDoneBanner({ workflowId }: { workflowId: UUID }) {
  const { workflow } = useWorkflowDetailLinks(workflowId);
  const [moveToColumn, { loading }] = Apollo.useMoveWorkflowToColumnMutation();
  const doneColumn = workflow?.doneColumn;

  return (
    <BannerShell
      action={
        doneColumn && (
          <AlertActions
            primary={{
              label: `Move to ${doneColumn.name}`,
              loading,
              // The click is the confirmation to leave Paused or Cancelled.
              onClick: () =>
                void moveToColumn({
                  refetchQueries: 'active',
                  variables: {
                    input: { confirmUnpark: true, props: { columnId: doneColumn.id }, workflowId },
                  },
                }),
            }}
          />
        )
      }
      heading="All tasks are done"
      icon={CircleCheck}
      severity="success"
      subtitle="Move this workflow to Done when you're ready to close it out."
    />
  );
}

function WorkflowStatusBannerInset({ children }: { children: React.ReactNode }) {
  const { classes } = useBannerStyles();
  return <div className={classes.bannerInset}>{children}</div>;
}

interface RequiredActionsBannerOrFallbackProps {
  phases: ApolloTypes.PhaseSimpleFragment[];
  workflowId: UUID;
  workflowState: ApolloTypes.WorkflowState | undefined | null;
}

function RequiredActionsBannerOrFallback({
  phases,
  workflowId,
  workflowState,
}: RequiredActionsBannerOrFallbackProps) {
  const hasIncompletePreSignTasks = useMemo(() => {
    const ordered = [...phases].sort((a, b) => a.order - b.order);
    for (const phase of ordered) {
      if (phase.__typename === 'SigningPhase') break;
      if (phase.tasks.length > 0 && !getAreAllTasksDone(phase.tasks)) return true;
    }
    return false;
  }, [phases]);

  switch (workflowState) {
    case Apollo.WorkflowState.Negotiating:
      return <NegotiatingBanner phases={phases} workflowId={workflowId} />;
    case Apollo.WorkflowState.ReadyForSignature:
      if (hasIncompletePreSignTasks)
        return <NegotiatingBanner phases={phases} workflowId={workflowId} />;
      return <ReadyForSignatureBanner workflowId={workflowId} />;
    case Apollo.WorkflowState.Signing:
      return <SigningBanner phases={phases} workflowId={workflowId} />;
    default:
      return null;
  }
}
